import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useFamily } from '@/hooks/useFamily';
import { useContacts, CreateContactData } from '@/hooks/useContacts';
import { useToast } from '@/hooks/use-toast';
import { useCurrentSection } from '@/hooks/useCurrentSection';
import { PageHeader } from '@/components/PageHeader';
import { NoFamilyMessage } from '@/components/NoFamilyMessage';

const contactTypeOptions = [
  { value: 'doctor', label: 'Médecin' },
  { value: 'pediatrician', label: 'Pédiatre' },
  { value: 'dentist', label: 'Dentiste' },
  { value: 'school', label: 'École' },
  { value: 'teacher', label: 'Enseignant(e)' },
  { value: 'babysitter', label: 'Baby-sitter' },
  { value: 'nanny', label: 'Nounou' },
  { value: 'daycare', label: 'Crèche' },
  { value: 'family_friend', label: 'Ami de la famille' },
  { value: 'neighbor', label: 'Voisin(e)' },
  { value: 'emergency', label: 'Urgence' },
  { value: 'pharmacy', label: 'Pharmacie' },
  { value: 'sports_club', label: 'Club de sport' },
  { value: 'other', label: 'Autre' },
];

export default function CreateContact() {
  const navigate = useNavigate();
  const { family, familyMembers } = useFamily();
  const { createContact } = useContacts();
  const { toast } = useToast();
  const { primaryVariant } = useCurrentSection();

  const [formData, setFormData] = useState<CreateContactData>({
    first_name: '',
    last_name: '',
    organization: '',
    contact_type: 'other',
    phone_primary: '',
    phone_secondary: '',
    email: '',
    address: '',
    notes: '',
    child_member_id: '',
    is_emergency_contact: false,
    availability_notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Clean up empty strings
      const cleanedData = {
        ...formData,
        organization: formData.organization || undefined,
        phone_primary: formData.phone_primary || undefined,
        phone_secondary: formData.phone_secondary || undefined,
        email: formData.email || undefined,
        address: formData.address || undefined,
        notes: formData.notes || undefined,
        child_member_id: formData.child_member_id || undefined,
        availability_notes: formData.availability_notes || undefined,
      };
      
      await createContact(cleanedData);

      toast({
        title: "Contact créé",
        description: "Le contact a été créé avec succès.",
      });

      navigate('/contacts');
    } catch (error) {
      console.error('Error creating contact:', error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de la création du contact.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof CreateContactData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const children = familyMembers.filter(member => !member.profile_id);

  if (!family) {
    return (
      <NoFamilyMessage 
        description="Vous devez d'abord créer une famille pour ajouter des contacts"
        buttonText="Créer ma famille"
      />
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader title="Nouveau contact" backPath="/contacts" />

      <div className="flex-1 p-4 pb-32">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">Prénom *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => updateField('first_name', e.target.value)}
                required
                placeholder="Jean"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Nom *</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => updateField('last_name', e.target.value)}
                required
                placeholder="Dupont"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="organization">Organisation / Établissement</Label>
            <Input
              id="organization"
              value={formData.organization}
              onChange={(e) => updateField('organization', e.target.value)}
              placeholder="Hôpital Saint-Louis, École Primaire..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_type">Type de contact *</Label>
            <Select 
              value={formData.contact_type} 
              onValueChange={(value) => updateField('contact_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un type" />
              </SelectTrigger>
              <SelectContent>
                {contactTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Child association */}
          {children.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="child_member">Contact spécifique à un enfant</Label>
              <Select 
                value={formData.child_member_id} 
                onValueChange={(value) => updateField('child_member_id', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Contact général de famille" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Contact général de famille</SelectItem>
                  {children.map((child) => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.child_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Contact details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone_primary">Téléphone principal</Label>
              <Input
                id="phone_primary"
                type="tel"
                value={formData.phone_primary}
                onChange={(e) => updateField('phone_primary', e.target.value)}
                placeholder="01 23 45 67 89"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone_secondary">Téléphone secondaire</Label>
              <Input
                id="phone_secondary"
                type="tel"
                value={formData.phone_secondary}
                onChange={(e) => updateField('phone_secondary', e.target.value)}
                placeholder="06 12 34 56 78"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="contact@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Adresse</Label>
            <Textarea
              id="address"
              value={formData.address}
              onChange={(e) => updateField('address', e.target.value)}
              placeholder="123 Rue de la Paix, 75001 Paris"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="availability_notes">Disponibilités / Horaires</Label>
            <Textarea
              id="availability_notes"
              value={formData.availability_notes}
              onChange={(e) => updateField('availability_notes', e.target.value)}
              placeholder="Lundi-Vendredi 9h-17h, Urgences 24h/24..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Informations supplémentaires..."
              rows={3}
            />
          </div>

          {/* Emergency contact flag */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_emergency_contact"
              checked={formData.is_emergency_contact}
              onCheckedChange={(checked) => updateField('is_emergency_contact', !!checked)}
            />
            <Label htmlFor="is_emergency_contact" className="text-sm font-medium">
              Contact d'urgence
            </Label>
          </div>
        </form>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 border-t bg-background space-y-3 z-50">
        <Button 
          onClick={handleSubmit}
          disabled={loading || !formData.first_name.trim() || !formData.last_name.trim()}
          className="w-full"
          variant={primaryVariant as any}
        >
          {loading ? 'Enregistrement...' : 'Créer le contact'}
        </Button>
        <Button 
          variant="cancel-hover" 
          onClick={() => navigate('/contacts')}
          className="w-full"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
}